import { useEffect, useState, type ReactNode } from 'react';
import {
  decodeIdToken,
  getStoredTokens,
  handleCallback,
  signOut,
  startSignIn,
} from './auth';
import {
  createPersona,
  deletePersona,
  getConfig,
  getInboxMessage,
  listInbox,
  listMessages,
  listPersonas,
  sendMessage,
  type Persona,
  type ReceivedMessage,
  type SentMessage,
} from './api';

type View = 'compose' | 'sent' | 'inbox' | 'personas';

export function App() {
  const [view, setView] = useState<View>('compose');
  const [authState, setAuthState] = useState<'checking' | 'signed-out' | 'signed-in' | 'callback' | 'error'>('checking');
  const [authError, setAuthError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  // ── Auth flow on mount ──────────────────────────────────────────────
  useEffect(() => {
    if (window.location.pathname === '/auth/callback') {
      setAuthState('callback');
      handleCallback()
        .then(({ returnTo }) => {
          window.history.replaceState(null, '', returnTo ?? '/');
          const tokens = getStoredTokens();
          if (tokens) {
            const claims = decodeIdToken(tokens.id_token);
            setUserEmail(typeof claims.email === 'string' ? claims.email : null);
            setAuthState('signed-in');
          } else {
            setAuthState('signed-out');
          }
        })
        .catch((err) => {
          setAuthError(err instanceof Error ? err.message : String(err));
          setAuthState('error');
        });
      return;
    }
    const tokens = getStoredTokens();
    if (tokens) {
      const claims = decodeIdToken(tokens.id_token);
      setUserEmail(typeof claims.email === 'string' ? claims.email : null);
      setAuthState('signed-in');
    } else {
      setAuthState('signed-out');
    }
  }, []);

  if (authState === 'checking' || authState === 'callback') {
    return <Centered>Načítavam…</Centered>;
  }

  if (authState === 'error') {
    return (
      <Centered>
        <div>
          <p style={{ color: 'crimson' }}>Sign-in failed: {authError}</p>
          <button onClick={() => startSignIn('/')}>Try again</button>
        </div>
      </Centered>
    );
  }

  if (authState === 'signed-out') {
    return (
      <Centered>
        <div>
          <h1>Mail Simulator</h1>
          <p>Internal QA tool for simulating Reklamacie email workflows.</p>
          <button onClick={() => startSignIn('/')}>Sign in with Cognito</button>
        </div>
      </Centered>
    );
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">📧 Mail Simulator</div>
        <nav>
          <button className={view === 'compose' ? 'active' : ''} onClick={() => setView('compose')}>
            Compose
          </button>
          <button className={view === 'sent' ? 'active' : ''} onClick={() => setView('sent')}>
            Sent log
          </button>
          <button className={view === 'inbox' ? 'active' : ''} onClick={() => setView('inbox')}>
            Inbox
          </button>
          <button className={view === 'personas' ? 'active' : ''} onClick={() => setView('personas')}>
            Personas
          </button>
        </nav>
        <div className="user">
          <span>{userEmail ?? 'signed in'}</span>
          <button onClick={signOut}>Sign out</button>
        </div>
      </header>
      <main className="main">
        {view === 'compose' && <ComposePage onSent={() => setView('sent')} />}
        {view === 'sent' && <SentPage onReply={() => setView('compose')} />}
        {view === 'inbox' && <InboxPage onReply={() => setView('compose')} />}
        {view === 'personas' && <PersonasPage />}
      </main>
    </div>
  );
}

// ─── Compose ───────────────────────────────────────────────────────────
function ComposePage({ onSent }: { onSent: () => void }) {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [recentMessages, setRecentMessages] = useState<SentMessage[]>([]);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [to, setTo] = useState('');
  const [fromPersonaId, setFromPersonaId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [inReplyToId, setInReplyToId] = useState('');
  // Reply-to-inbox path: when set, sendMessage threads off this inbound row
  // instead of a SentMessage. Mutually exclusive with inReplyToId.
  const [inboundParentId, setInboundParentId] = useState('');
  const [inboundParentSubject, setInboundParentSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([listPersonas(), listMessages(), getConfig()])
      .then(([p, m, c]) => {
        setPersonas(p.personas);
        setRecentMessages(m.messages);
        setRecipients(c.recipients);
        setTo(c.defaultRecipient);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  // Personas valid for the currently-selected recipient. Universal personas
  // (allowedRecipients undefined/empty) are always shown.
  const availablePersonas = personas.filter(
    (p) => !p.allowedRecipients?.length || p.allowedRecipients.includes(to),
  );

  // If the selected From persona becomes invalid (e.g., recipient swapped),
  // drop it so the user is forced to pick a valid one.
  useEffect(() => {
    if (fromPersonaId && !availablePersonas.some((p) => p.id === fromPersonaId)) {
      setFromPersonaId('');
    }
  }, [to, personas, fromPersonaId, availablePersonas]);

  // Pre-fill if a "?reply=<id>" is present in the URL (set by Sent page Reply button)
  useEffect(() => {
    const url = new URL(window.location.href);
    const replyId = url.searchParams.get('reply');
    if (replyId) {
      setInReplyToId(replyId);
      const parent = recentMessages.find((m) => m.id === replyId);
      if (parent) {
        setSubject(parent.subject.startsWith('Re: ') ? parent.subject : `Re: ${parent.subject}`);
      }
      url.searchParams.delete('reply');
      window.history.replaceState(null, '', url.pathname + (url.search || ''));
    }
  }, [recentMessages]);

  // Pre-fill if a "?reply-inbound=<id>" is present (set by Inbox page Reply
  // button). Fetches the inbound message and pre-fills:
  //   - From persona = matchedPersonaIds[0] (the persona that received it)
  //   - To = that persona's allowedRecipients[0] — NOT the inbound From,
  //     because Reklamacie sends notification mail from services.fortwana.sk
  //     (Resend) which is outside our SES outbound IAM allowlist. The
  //     persona-binding tells us which allowlist recipient is the natural
  //     correspondent for this customer/subcontractor.
  //   - Subject = "Re: <original>"
  //   - inboundParentId so the outbound In-Reply-To threads back correctly.
  // Waits for both `recipients` and `personas` to be loaded so we can look
  // up the binding before deciding `to`.
  useEffect(() => {
    if (recipients.length === 0 || personas.length === 0) return;
    const url = new URL(window.location.href);
    const inboundId = url.searchParams.get('reply-inbound');
    if (!inboundId) return;
    url.searchParams.delete('reply-inbound');
    window.history.replaceState(null, '', url.pathname + (url.search || ''));
    getInboxMessage(inboundId)
      .then(({ message }) => {
        setInboundParentId(message.id);
        setInboundParentSubject(message.subject);
        setSubject(message.subject.startsWith('Re: ') ? message.subject : `Re: ${message.subject}`);
        if (message.matchedPersonaIds.length > 0) {
          const personaId = message.matchedPersonaIds[0];
          setFromPersonaId(personaId);
          const persona = personas.find((p) => p.id === personaId);
          const bound = persona?.allowedRecipients?.[0];
          if (bound && recipients.includes(bound)) {
            setTo(bound);
          }
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [recipients, personas]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!fromPersonaId) {
      setError('Vyberte odosielateľa.');
      return;
    }
    setSending(true);
    try {
      await sendMessage({
        fromPersonaId,
        subject,
        body,
        to: to || undefined,
        inReplyToId: inReplyToId || undefined,
        inboundParentId: inboundParentId || undefined,
      });
      setSubject('');
      setBody('');
      setInReplyToId('');
      setInboundParentId('');
      setInboundParentSubject('');
      onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  };

  const clearInboundReply = () => {
    setInboundParentId('');
    setInboundParentSubject('');
  };

  return (
    <form className="card" onSubmit={handleSend}>
      <h2>Compose simulated email</h2>
      {inboundParentId && (
        <div className="hint">
          ↳ Replying to inbox message: <strong>{inboundParentSubject || '(no subject)'}</strong>{' '}
          <button type="button" className="link" onClick={clearInboundReply}>
            clear
          </button>
        </div>
      )}
      <label>
        To (recipient)
        <select value={to} onChange={(e) => setTo(e.target.value)} required>
          {recipients.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </label>
      <label>
        From persona
        <select value={fromPersonaId} onChange={(e) => setFromPersonaId(e.target.value)} required>
          <option value="">— vyberte —</option>
          {availablePersonas.map((p) => (
            <option key={p.id} value={p.id}>
              [{p.role}] {p.displayName} &lt;{p.emailLocalPart}@…&gt;
            </option>
          ))}
        </select>
        {availablePersonas.length === 0 && personas.length > 0 && (
          <small className="hint">No personas are configured for <code>{to}</code>. Bind some via the Personas tab.</small>
        )}
      </label>
      <label>
        Reply to (optional — for threading)
        <select value={inReplyToId} onChange={(e) => setInReplyToId(e.target.value)}>
          <option value="">— new thread —</option>
          {recentMessages.slice(0, 20).map((m) => (
            <option key={m.id} value={m.id}>
              {m.sentAt.slice(0, 16)} | {m.fromDisplayName} | {m.subject.slice(0, 60)}
            </option>
          ))}
        </select>
      </label>
      <label>
        Subject
        <input
          type="text"
          maxLength={200}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
        />
      </label>
      <label>
        Body
        <textarea
          rows={12}
          maxLength={10000}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          required
        />
      </label>
      {error && <div className="error">{error}</div>}
      <div className="actions">
        <button type="submit" disabled={sending}>
          {sending ? 'Sending…' : `Send to ${to || '—'}`}
        </button>
      </div>
    </form>
  );
}

// ─── Sent log ──────────────────────────────────────────────────────────
function SentPage({ onReply }: { onReply: () => void }) {
  const [messages, setMessages] = useState<SentMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState<SentMessage | null>(null);

  const refresh = () => {
    setRefreshing(true);
    listMessages()
      .then((r) => setMessages(r.messages))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setRefreshing(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  // Close the detail modal on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelected(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const reply = (id: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('reply', id);
    window.history.replaceState(null, '', url.pathname + url.search);
    onReply();
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>Sent log (most recent 50)</h2>
        <button onClick={refresh} disabled={refreshing}>
          {refreshing ? '…' : 'Refresh'}
        </button>
      </div>
      {error && <div className="error">{error}</div>}
      {messages.length === 0 ? (
        <p>Žiadne odoslané správy.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Sent at (UTC)</th>
              <th>From</th>
              <th>To</th>
              <th>Subject</th>
              <th>Threading</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {messages.map((m) => (
              <tr key={m.id}>
                <td>{m.sentAt.slice(0, 19).replace('T', ' ')}</td>
                <td>
                  {m.fromDisplayName}
                  <br />
                  <small>{m.fromAddress}</small>
                </td>
                <td>
                  <small>{m.recipient}</small>
                </td>
                <td>
                  <button className="link" onClick={() => setSelected(m)}>
                    {m.subject}
                  </button>
                </td>
                <td>{m.inReplyToMessageId ? '↳ reply' : 'new'}</td>
                <td className="row-actions">
                  <button onClick={() => setSelected(m)}>View</button>
                  <button onClick={() => reply(m.id)}>Reply</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {selected && (
        <MessageDetail
          message={selected}
          onClose={() => setSelected(null)}
          onReply={() => reply(selected.id)}
        />
      )}
    </div>
  );
}

// ─── Sent message detail (modal) ─────────────────────────────────────────
function MessageDetail({
  message,
  onClose,
  onReply,
}: {
  message: SentMessage;
  onClose: () => void;
  onReply: () => void;
}) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{message.subject}</h3>
          <button onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <dl className="modal-meta">
          <dt>From</dt>
          <dd>
            {message.fromDisplayName} &lt;{message.fromAddress}&gt;
          </dd>
          <dt>To</dt>
          <dd>{message.recipient}</dd>
          <dt>Sent at (UTC)</dt>
          <dd>{message.sentAt.slice(0, 19).replace('T', ' ')}</dd>
          <dt>Message-ID</dt>
          <dd>
            <code>{message.messageId}</code>
          </dd>
          {message.inReplyToMessageId && (
            <>
              <dt>In-Reply-To</dt>
              <dd>
                <code>{message.inReplyToMessageId}</code>
              </dd>
            </>
          )}
          {message.references && (
            <>
              <dt>References</dt>
              <dd>
                <code>{message.references}</code>
              </dd>
            </>
          )}
        </dl>
        <pre className="modal-body">{message.body}</pre>
        <div className="actions">
          <button onClick={onReply}>Reply</button>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Inbox (received) ─────────────────────────────────────────────────
function InboxPage({ onReply }: { onReply: () => void }) {
  const [messages, setMessages] = useState<ReceivedMessage[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selected, setSelected] = useState<ReceivedMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = () => {
    setRefreshing(true);
    listInbox()
      .then((r) => setMessages(r.messages))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setRefreshing(false));
  };

  useEffect(() => {
    refresh();
    listPersonas()
      .then((p) => setPersonas(p.personas))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelected(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const reply = (id: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('reply-inbound', id);
    window.history.replaceState(null, '', url.pathname + url.search);
    onReply();
  };

  const personaName = (id: string) => personas.find((p) => p.id === id)?.displayName ?? id;
  // Reply is available whenever we resolved at least one matched persona. The
  // outbound "to" defaults to that persona's allowedRecipients[0] (computed in
  // ComposePage when the reply-inbound flow runs), so the inbound From address
  // (which may be on services.fortwana.sk and outside our IAM allowlist) is
  // irrelevant to the gate.
  const canReply = (m: ReceivedMessage) => m.matchedPersonaIds.length > 0;

  return (
    <div className="card">
      <div className="card-header">
        <h2>Inbox (most recent 50)</h2>
        <button onClick={refresh} disabled={refreshing}>
          {refreshing ? '…' : 'Refresh'}
        </button>
      </div>
      {error && <div className="error">{error}</div>}
      {messages.length === 0 ? (
        <p>Žiadne prijaté správy. (Replies to <code>*@sim.aws.fortwana.sk</code> land here.)</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Received (UTC)</th>
              <th>From</th>
              <th>To (matched persona)</th>
              <th>Subject</th>
              <th>Threading</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {messages.map((m) => (
              <tr key={m.id}>
                <td>{m.receivedAt.slice(0, 19).replace('T', ' ')}</td>
                <td>
                  {m.fromDisplayName || m.fromAddress}
                  {m.fromDisplayName && (
                    <>
                      <br />
                      <small>{m.fromAddress}</small>
                    </>
                  )}
                </td>
                <td>
                  {m.matchedPersonaIds.length > 0
                    ? m.matchedPersonaIds.map((pid) => <div key={pid}><small>{personaName(pid)}</small></div>)
                    : m.toAddresses.map((a) => <div key={a}><small className="muted">{a}</small></div>)}
                </td>
                <td>
                  <button className="link" onClick={() => setSelected(m)}>
                    {m.subject || '(no subject)'}
                  </button>
                </td>
                <td>{m.inReplyToMessageId ? '↳ reply' : 'new'}</td>
                <td className="row-actions">
                  <button onClick={() => setSelected(m)}>View</button>
                  {canReply(m) && <button onClick={() => reply(m.id)}>Reply</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {selected && (
        <InboxDetail
          message={selected}
          personaName={personaName}
          canReply={canReply(selected)}
          onClose={() => setSelected(null)}
          onReply={() => reply(selected.id)}
        />
      )}
    </div>
  );
}

function InboxDetail({
  message,
  personaName,
  canReply,
  onClose,
  onReply,
}: {
  message: ReceivedMessage;
  personaName: (id: string) => string;
  canReply: boolean;
  onClose: () => void;
  onReply: () => void;
}) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{message.subject || '(no subject)'}</h3>
          <button onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <dl className="modal-meta">
          <dt>From</dt>
          <dd>
            {message.fromDisplayName ? `${message.fromDisplayName} ` : ''}
            &lt;{message.fromAddress}&gt;
          </dd>
          <dt>To</dt>
          <dd>
            {message.toAddresses.map((a) => (
              <div key={a}>{a}</div>
            ))}
          </dd>
          {message.matchedPersonaIds.length > 0 && (
            <>
              <dt>Matched personas</dt>
              <dd>
                {message.matchedPersonaIds.map((pid) => (
                  <div key={pid}>{personaName(pid)}</div>
                ))}
              </dd>
            </>
          )}
          <dt>Received (UTC)</dt>
          <dd>{message.receivedAt.slice(0, 19).replace('T', ' ')}</dd>
          <dt>Message-ID</dt>
          <dd>
            <code>{message.messageId || '—'}</code>
          </dd>
          {message.inReplyToMessageId && (
            <>
              <dt>In-Reply-To</dt>
              <dd>
                <code>{message.inReplyToMessageId}</code>
              </dd>
            </>
          )}
          {message.references && (
            <>
              <dt>References</dt>
              <dd>
                <code>{message.references}</code>
              </dd>
            </>
          )}
          <dt>Raw S3 key</dt>
          <dd>
            <code>{message.rawS3Key}</code>
          </dd>
        </dl>
        <pre className="modal-body">{message.body || '(empty body)'}</pre>
        <div className="actions">
          {canReply && <button onClick={onReply}>Reply</button>}
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Personas ──────────────────────────────────────────────────────────
function PersonasPage() {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    role: 'customer' as Persona['role'],
    displayName: '',
    emailLocalPart: '',
    phone: '',
    unitNumber: '',
    category: '',
    allowedRecipients: [] as string[],
  });

  const refresh = () => {
    listPersonas()
      .then((r) => setPersonas(r.personas))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(() => {
    refresh();
    getConfig()
      .then((c) => setRecipients(c.recipients))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const toggleAllowedRecipient = (r: string) => {
    setForm((f) => ({
      ...f,
      allowedRecipients: f.allowedRecipients.includes(r)
        ? f.allowedRecipients.filter((x) => x !== r)
        : [...f.allowedRecipients, r],
    }));
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setAdding(true);
    try {
      await createPersona({
        role: form.role,
        displayName: form.displayName,
        emailLocalPart: form.emailLocalPart,
        phone: form.phone || undefined,
        unitNumber: form.unitNumber || undefined,
        category: form.category || undefined,
        allowedRecipients: form.allowedRecipients.length > 0 ? form.allowedRecipients : undefined,
      });
      setForm({ role: 'customer', displayName: '', emailLocalPart: '', phone: '', unitNumber: '', category: '', allowedRecipients: [] });
      setShowAdd(false);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete persona "${name}"?\n\nNote: the matching customer/subcontractor record in Reklamacie test project must be removed manually via Studio.`)) {
      return;
    }
    try {
      await deletePersona(id);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>Personas</h2>
        <button onClick={() => setShowAdd(!showAdd)}>{showAdd ? 'Cancel' : 'Add persona'}</button>
      </div>
      {error && <div className="error">{error}</div>}

      {showAdd && (
        <form className="add-form" onSubmit={handleAdd}>
          <label>
            Role
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Persona['role'] })}>
              <option value="customer">customer</option>
              <option value="subcontractor">subcontractor</option>
              <option value="internal">internal</option>
            </select>
          </label>
          <label>
            Display name
            <input
              type="text"
              maxLength={100}
              required
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              placeholder="Anna Nováková"
            />
          </label>
          <label>
            Email local part (sender address: &lt;local&gt;@sim.aws.fortwana.sk)
            <input
              type="text"
              required
              pattern="[a-z0-9][a-z0-9._-]{0,62}"
              value={form.emailLocalPart}
              onChange={(e) => setForm({ ...form, emailLocalPart: e.target.value })}
              placeholder="customer-anna"
            />
          </label>
          <label>
            Phone (optional)
            <input
              type="text"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </label>
          <label>
            Unit number (optional, customers only)
            <input
              type="text"
              value={form.unitNumber}
              onChange={(e) => setForm({ ...form, unitNumber: e.target.value })}
              placeholder="3B"
            />
          </label>
          <label>
            Category (optional, subcontractors only)
            <input
              type="text"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="elektroinštalácia"
            />
          </label>
          <fieldset className="recipient-fieldset">
            <legend>Restrict to recipients (leave empty for universal)</legend>
            {recipients.map((r) => (
              <label key={r} className="checkbox-row">
                <input
                  type="checkbox"
                  checked={form.allowedRecipients.includes(r)}
                  onChange={() => toggleAllowedRecipient(r)}
                />
                <code>{r}</code>
              </label>
            ))}
          </fieldset>
          <div className="hint">
            ⚠️ Reminder: after adding here, also add a matching customer or subcontractor record in Reklamacie Studio →
            test project → Zákazníci/Subdodávatelia, with email{' '}
            <code>{form.emailLocalPart || '<local-part>'}@sim.aws.fortwana.sk</code>. Otherwise the
            Reklamacie email-match logic won't resolve the sender.
          </div>
          <button type="submit" disabled={adding}>
            {adding ? 'Adding…' : 'Add persona'}
          </button>
        </form>
      )}

      <table>
        <thead>
          <tr>
            <th>Role</th>
            <th>Display name</th>
            <th>Email</th>
            <th>Bound to</th>
            <th>Extra</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {personas.map((p) => (
            <tr key={p.id}>
              <td>
                <code>{p.role}</code>
              </td>
              <td>{p.displayName}</td>
              <td>
                <code>{p.emailLocalPart}@sim.aws.fortwana.sk</code>
              </td>
              <td>
                {p.allowedRecipients && p.allowedRecipients.length > 0 ? (
                  p.allowedRecipients.map((r) => (
                    <div key={r}>
                      <small>{r}</small>
                    </div>
                  ))
                ) : (
                  <small className="muted">— universal —</small>
                )}
              </td>
              <td>
                {p.phone && <div>📞 {p.phone}</div>}
                {p.unitNumber && <div>🏠 {p.unitNumber}</div>}
                {p.category && <div>🔧 {p.category}</div>}
              </td>
              <td>
                <button onClick={() => handleDelete(p.id, p.displayName)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Layout helper ─────────────────────────────────────────────────────
function Centered({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      {children}
    </div>
  );
}
